/* eslint-disable react-hooks/immutability */
import { formatMoney } from 'betterplace-utils'
import { Chart, Filler, LineController, LineElement, Title, Tooltip } from 'chart.js'
import React, { useEffect, useRef, useState } from 'react'
import { DashboardChartProps } from './types'
import { I18nHtml, JSONFetcher, themeColor } from 'src/shared'

Chart.register(LineElement, LineController, Title, Tooltip, Filler)

Chart.defaults.elements.line.borderColor = themeColor('purple-600')
Chart.defaults.elements.line.fill = true
Chart.defaults.elements.line.backgroundColor = themeColor('green-500')

export const DashboardChart = (props: DashboardChartProps): JSX.Element => {
  const [response, setResponse] = useState<Chart['data'] | undefined>(undefined)
  const canvasRef: React.MutableRefObject<HTMLCanvasElement | null> = useRef(null)
  const [chart, setChart] = useState<Chart | undefined>(undefined)

  useEffect(() => {
    if (!canvasRef.current) return
    setChart(
      new Chart(canvasRef.current, {
        type: 'line',
        data: {
          labels: [],
          datasets: [],
        },
        options: {
          aspectRatio: 1.92,
          plugins: {
            title: {
              display: true,
              position: 'bottom',
              font: {
                family: '"Fira Sans", sans-serif',
                size: 20,
                weight: 'bold',
              },
              color: themeColor('gray-900'),
              align: 'center',
            },
            legend: {
              display: false,
            },
            tooltip: {
              displayColors: false,
              backgroundColor: 'white',
              titleColor: 'black',
              bodyColor: 'black',
              borderWidth: 1,
              borderColor: themeColor('gray-300'),
              padding: 12,
              cornerRadius: 4,
            },
          },
          scales: {
            y: {
              min: 0,
              ticks: {
                precision: 0,
                padding: 8,
                color: 'black',
                callback: (value) => formatMoney(i18n.locale, parseFloat(`${value}`), { precision: 0 }),
              },
              grid: {
                drawTicks: false,
              },
            },
            x: {
              ticks: {
                padding: 8,
                color: 'black',
                font: { weight: 'bold' },
              },
              grid: {
                drawTicks: false,
                drawOnChartArea: false,
              },
              offset: true,
            },
          },
        },
      })
    )
  }, [])

  // Fetch data
  useEffect(() => {
    JSONFetcher.put({
      url: props.apiUrl,
      success: setResponse,
    })
  }, [props.apiUrl])

  // Update chart once data is loaded and chart is initialized
  useEffect(() => {
    if (chart && response) {
      chart.data.labels = response.labels
      chart.data.datasets = response.datasets
      // FIXME value on the interface possibly undefined, after transition to TS replace @ts-ignore flags with non-null assertions

      chart.options.plugins!.title!.text = i18n.t(`manage.dashboard_chart.dimension_label`)
      chart.update()
    }
  }, [response, chart])

  // Update label function if dimension changes
  useEffect(() => {
    if (chart) {
      // FIXME value on the interface possibly undefined, after transition to TS replace @ts-ignore flags with non-null assertions
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      chart.options.plugins.tooltip.callbacks.label = (item) => {
        const value = formatMoney(i18n.locale, parseFloat((item.raw as string) ?? ''))
        const label = i18n.t(`manage.dashboard_chart.tooltip_label`)
        return `${label} : ${value}`
      }
    }
  }, [response, chart])

  return (
    <>
      <div style={{ backgroundColor: themeColor('gray-200'), padding: '1.5rem', borderRadius: '3px' }}>
        <div className="chart-container mt-6 w-full" style={{ position: 'relative' }}>
          <canvas ref={canvasRef} />
        </div>
      </div>
      <I18nHtml
        as="div"
        className="mt-2 mb-12 text-right"
        i18nKey="manage.dashboard_chart.statistics_teaser_html"
        options={{ donation_statistics_url: props.detailedChartPath }}
      />
    </>
  )
}
